import { v, type Infer } from "convex/values";

export const recordTotalValidator = v.union(
  v.object({ kind: v.literal("exact"), value: v.number() }),
  v.object({ kind: v.literal("atLeast"), value: v.number() }),
  v.object({ kind: v.literal("unknown") }),
);

export type RecordTotal = Infer<typeof recordTotalValidator>;

export const UNKNOWN_RECORD_TOTAL: RecordTotal = { kind: "unknown" };

export function exactRecordTotal(total: RecordTotal): number | undefined {
  return total.kind === "exact" ? total.value : undefined;
}

export function formatRecordTotal(total: RecordTotal): string | null {
  if (total.kind === "unknown") return null;
  const formatted = total.value.toLocaleString();
  return total.kind === "atLeast" ? `${formatted}+` : formatted;
}

export function canLoadMoreRecords(args: {
  offset: number;
  limit: number;
  matched: number;
  total: RecordTotal;
  scanCanExpand?: boolean;
}): boolean {
  return (
    args.offset + args.limit < args.matched ||
    (args.scanCanExpand === true && args.total.kind === "atLeast")
  );
}

export function recordTotalFromSaturation(args: {
  matched: number;
  saturated: boolean;
}): RecordTotal {
  return args.saturated
    ? { kind: "atLeast", value: args.matched }
    : { kind: "exact", value: args.matched };
}

export function sumRecordTotals(totals: RecordTotal[]): RecordTotal {
  let value = 0;
  let saturated = false;
  for (const total of totals) {
    if (total.kind === "unknown") return UNKNOWN_RECORD_TOTAL;
    value += total.value;
    saturated = saturated || total.kind === "atLeast";
  }
  return recordTotalFromSaturation({ matched: value, saturated });
}

export function recordTotalFromScan(args: {
  matched: number;
  scanned: number;
  scanCap: number;
}): RecordTotal {
  return recordTotalFromSaturation({
    matched: args.matched,
    saturated: args.scanned >= args.scanCap,
  });
}
